/**
 * StreamingChatMessage Component
 *
 * Displays individual messages in segment chat with streaming state awareness.
 * Shows user and assistant messages with appropriate styling.
 * Supports real-time streaming content display with animated indicator.
 * Assistant messages are rendered with Markdown formatting.
 */

import { formatDistanceToNow } from "date-fns";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import type {
  SegmentChatMessageResponse,
  ChatMessageRole,
} from "@/types/segmentChat";

interface StreamingChatMessageProps {
  /** Saved message from history (undefined when streaming new message) */
  message?: SegmentChatMessageResponse;
  /** Whether this message is currently being streamed */
  isStreaming?: boolean;
  /** Content to display (used for streaming or optimistic UI) */
  streamingContent?: string;
  /** Role override (used when showing streaming message or optimistic UI) */
  role?: ChatMessageRole;
  /** Compact mode for inline chat (smaller avatars, less padding) */
  compact?: boolean;
}

export function StreamingChatMessage({
  message,
  isStreaming = false,
  streamingContent,
  role,
  compact = false,
}: StreamingChatMessageProps) {
  // Determine message role
  const messageRole = role || message?.role || "assistant";
  const isUser = messageRole === "user";

  // Determine content to display:
  // - Use streamingContent if provided (for streaming or optimistic UI)
  // - Otherwise fall back to message content
  const content = streamingContent ?? message?.content ?? "";

  // Format timestamp (only for saved messages)
  const timestamp = message?.created_at
    ? formatDistanceToNow(new Date(message.created_at), { addSuffix: true })
    : null;

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} ${compact ? "mb-2" : "mb-4"} group`}
    >
      <div
        className={`flex ${compact ? "gap-2" : "gap-3"} max-w-[85%] ${isUser ? "flex-row-reverse" : "flex-row"}`}
      >
        {/* Avatar */}
        <div
          className={`flex-shrink-0 rounded-full flex items-center justify-center ${
            compact ? "w-6 h-6" : "w-8 h-8"
          } ${
            isUser
              ? "bg-primary text-primary-foreground"
              : "bg-purple-600 dark:bg-purple-700 text-white"
          }`}
        >
          <span
            className={compact ? "text-xs font-medium" : "text-sm font-medium"}
          >
            {isUser ? "Y" : "T"}
          </span>
        </div>

        {/* Message content */}
        <div className="flex flex-col gap-1">
          <div
            className={`rounded-2xl ${compact ? "px-3 py-2" : "px-4 py-3"} ${
              isUser
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground border border-border"
            }`}
          >
            {isUser ? (
              // User messages: plain text
              <div
                className={`whitespace-pre-wrap leading-relaxed ${compact ? "text-xs" : "text-sm"}`}
              >
                {content}
              </div>
            ) : (
              // Assistant messages: markdown formatted
              <div
                className={`leading-relaxed ${compact ? "text-xs" : "text-sm"} prose prose-sm max-w-none dark:prose-invert [&>p]:mb-1.5 [&>p:last-child]:mb-0 [&>ul]:my-1.5 [&>ol]:my-1.5 [&>li]:my-0.5 ${compact ? "[&>p]:text-xs [&>li]:text-xs" : ""}`}
              >
                <ReactMarkdown>{content}</ReactMarkdown>
                {/* Streaming indicator */}
                {isStreaming && (
                  <span className="inline-flex items-center ml-1">
                    <Loader2
                      className={
                        compact
                          ? "h-2.5 w-2.5 animate-spin"
                          : "h-3 w-3 animate-spin"
                      }
                    />
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Timestamp - only show for saved messages (not in compact mode) */}
          {timestamp && !compact && (
            <div
              className={`text-xs text-muted-foreground px-2 opacity-0 group-hover:opacity-100 transition-opacity ${
                isUser ? "text-right" : "text-left"
              }`}
            >
              {timestamp}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
